import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useClub } from "@/contexts/ClubContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "../components/ui/badge";
import { CalendarDays, Clock, Download, ArrowLeft, MapPin } from "lucide-react";
import { LinkifyText } from "@/components/LinkifyText";
import { ImageLightbox } from "@/components/ImageLightbox";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetailPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const { clubs, getClubAccent } = useClub();
  usePageTitle(event?.title || "Event");

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/events`).then(r => {
      const found = r.data.find(e => e.id === id);
      if (found) setEvent(found);
    }).catch(() => {});
  }, [id]);

  if (!event) return null;

  const club = clubs.find(c => c.id === event.club_id);
  const accent = getClubAccent(event.club_id);
  const imgSrc = resolveImageUrl(event.image_url);
  const dateFormatted = new Date(event.date).toLocaleDateString("de-AT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="py-12 md:py-24 px-4 md:px-8" data-testid="event-detail-page">
      <div className="max-w-3xl mx-auto">
        <Link to={`/kalender?event=${event.id}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-to-calendar">
          <ArrowLeft className="w-4 h-4" /> Zurück zum Kalender
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Badge style={{ backgroundColor: accent, color: '#09090b' }} className="text-xs font-bold uppercase">
              {club?.name || event.club_id}
            </Badge>
            {event.featured && <Badge variant="outline" className="text-xs">Featured</Badge>}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
            {event.title}
          </h1>
        </div>

        {/* Meta Info */}
        <div className="bg-card border border-white/5 p-5 mb-6 flex flex-wrap gap-6" data-testid="event-meta">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" style={{ color: accent }} />
            <div>
              <p className="text-xs text-muted-foreground">Datum</p>
              <p className="text-sm font-medium">{dateFormatted}</p>
            </div>
          </div>
          {event.time && (
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" style={{ color: accent }} />
              <div>
                <p className="text-xs text-muted-foreground">Uhrzeit</p>
                <p className="text-sm font-medium">{event.time}{event.end_time ? ` - ${event.end_time}` : ''} Uhr</p>
              </div>
            </div>
          )}
        </div>

        {/* Image */}
        {imgSrc && (
          <div className="mb-6">
            <img src={imgSrc} alt={event.title}
              onClick={() => setLightbox(true)}
              onError={e => { e.target.parentElement.style.display = 'none'; }}
              className="max-w-full max-h-[300px] object-contain cursor-pointer hover:opacity-90 transition-opacity border border-white/10"
              data-testid="event-detail-image" />
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="bg-card border border-white/5 p-6 mb-6" data-testid="event-description">
            <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed"><LinkifyText text={event.description} /></p>
          </div>
        )}

        {/* Availability */}
        {(event.billard_available === false || event.edart_available === false || event.steeldart_available === false) && (
          <div className="bg-card border border-white/5 p-5 mb-6" data-testid="event-availability">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Verfügbarkeit für Gäste</h3>
            <div className="flex flex-wrap gap-3">
              <Badge variant={event.billard_available !== false ? "default" : "outline"} className={`text-xs ${event.billard_available !== false ? "bg-green-600" : "text-destructive border-destructive/30"}`}>
                Billard: {event.billard_available !== false ? "Verfügbar" : "Nicht verfügbar"}
              </Badge>
              <Badge variant={event.edart_available !== false ? "default" : "outline"} className={`text-xs ${event.edart_available !== false ? "bg-green-600" : "text-destructive border-destructive/30"}`}>
                EDart: {event.edart_available !== false ? "Verfügbar" : "Nicht verfügbar"}
              </Badge>
              <Badge variant={event.steeldart_available !== false ? "default" : "outline"} className={`text-xs ${event.steeldart_available !== false ? "bg-green-600" : "text-destructive border-destructive/30"}`}>
                SteelDart: {event.steeldart_available !== false ? "Verfügbar" : "Nicht verfügbar"}
              </Badge>
            </div>
          </div>
        )}

        {/* ICS Download */}
        <a href={`${API}/events/${event.id}/ical`} download
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-sm font-bold uppercase hover:-translate-y-0.5 transition-transform"
          data-testid="event-ics-download">
          <Download className="w-4 h-4" /> Zum Kalender hinzufügen (.ics)
        </a>
      </div>

      {/* Lightbox */}
      {lightbox && imgSrc && (
        <ImageLightbox src={imgSrc} alt={event.title} onClose={() => setLightbox(false)} />
      )}
    </div>
  );
}
